/**
 * Auth Callback Page
 * Handles the redirect back from the Slope server after Azure Entra ID sign-in.
 *
 * The server puts a one-minute, single-use login code in this page's URL and
 * nothing else; the page exchanges it for the session over POST. The session
 * ID itself is therefore never part of a URL — not in history, not in a
 * Referer, not in an access log.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/auth';
import { useAuth } from '../contexts/AuthContext';
import { safeRedirectPath } from '../lib/safeRedirect';

/** What the user sees for each error code the server can send back. */
const ERROR_MESSAGES: Record<string, string> = {
  access_denied: 'Sign-in was cancelled.',
  invalid_state: 'This sign-in attempt has expired. Please try again.',
  invalid_request: 'The sign-in response was incomplete. Please try again.',
  authentication_failed: 'Sign-in could not be completed. Please try again or contact IT.',
};

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();
  const [error, setError] = useState<string | null>(null);

  // The login code is single-use, and React StrictMode runs effects twice in
  // development, so the exchange must be attempted exactly once per mount.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCallback = async () => {
    const code = searchParams.get('code');
    const redirectUrl = searchParams.get('redirect');
    const errorCode = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    const failWith = (message: string) => {
      setError(message);
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    };

    if (errorCode) {
      console.error('Auth error:', errorCode, errorDescription);
      failWith(ERROR_MESSAGES[errorCode] ?? errorDescription ?? 'Authentication failed');
      return;
    }

    if (!code) {
      failWith('Missing login code');
      return;
    }

    try {
      const { sessionId } = await authService.exchangeLoginCode(code);
      authService.setSessionId(sessionId);

      // AuthProvider checked for a session when the page loaded, before the
      // exchange finished. Let it see the new one before entering a protected
      // route, or ProtectedRoute would bounce straight back to /login.
      await refreshAuth();

      // The server has already vetted this; checking again costs nothing.
      navigate(safeRedirectPath(redirectUrl), { replace: true });
    } catch (err) {
      console.error('Authentication error:', err);
      failWith(err instanceof Error ? err.message : 'Authentication failed');
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream dark:bg-midnight">
        <div className="max-w-md w-full p-8">
          <div className="card">
            <div className="card-body text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-rust/10 dark:bg-salmon/10 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-rust dark:text-salmon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-slate dark:text-mist">
                Authentication Failed
              </h2>
              <p className="text-sm text-chalkboard dark:text-rain-cloud">
                {error}
              </p>
              <p className="text-xs text-chalkboard dark:text-rain-cloud">
                Redirecting to login page...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream dark:bg-midnight">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pond dark:border-pond-light mx-auto"></div>
        <p className="mt-4 text-slate dark:text-mist">Completing sign in...</p>
      </div>
    </div>
  );
}
